import { useEffect, useRef, useState } from 'react';
import { useNavigateWithParams } from '@/shared/hooks/use-navigate-with-params';
import { ANALYTICS_EVENTS, trackEvent } from '@/features/analytics';
import { usePreloadQuizImages } from '@/features/quiz/hooks/use-preload-quiz-images';
import { usePreloadOnboardingImages } from '../hooks/use-preload-onboarding-images';
import { ONBOARDING_STEP_COUNT } from '../types';
import { OnboardingStep1 } from './steps/step-1';
import { OnboardingStep2 } from './steps/step-2';
import { OnboardingStep3 } from './steps/step-3';
import { OnboardingStep4 } from './steps/step-4';
import { OnboardingStep5 } from './steps/step-5';
import { OnboardingStep6 } from './steps/step-6';

const STEPS = [
  OnboardingStep1,
  OnboardingStep2,
  OnboardingStep3,
  OnboardingStep4,
  OnboardingStep5,
  OnboardingStep6,
] as const;

const ONBOARDING_CONTINUE_EVENTS = [
  ANALYTICS_EVENTS.SETTLEMENT_ONBOARDING_1OWES_YOU,
  ANALYTICS_EVENTS.SETTLEMENT_ONBOARDING_2SETTLEMENTS,
  ANALYTICS_EVENTS.SETTLEMENT_ONBOARDING_3FILE_ONCE,
  ANALYTICS_EVENTS.SETTLEMENT_ONBOARDING_4FINES,
  ANALYTICS_EVENTS.SETTLEMENT_ONBOARDING_5UNCLAIMED,
  ANALYTICS_EVENTS.SETTLEMENT_ONBOARDING_6FIND_OUT,
] as const;

export function OnboardingFlow() {
  const navigate = useNavigateWithParams();
  const [stepIndex, setStepIndex] = useState(0);
  const firedLoaded = useRef(false);

  // Warm later onboarding + quiz images while the user is still reading step 1.
  usePreloadOnboardingImages();
  usePreloadQuizImages();

  useEffect(() => {
    if (firedLoaded.current) return;
    firedLoaded.current = true;
    trackEvent(ANALYTICS_EVENTS.SETTLEMENT_ONBOARDING_LOADED);
  }, []);

  const goNext = () => {
    const event = ONBOARDING_CONTINUE_EVENTS[stepIndex];
    if (event) trackEvent(event);
    if (stepIndex >= ONBOARDING_STEP_COUNT - 1) {
      navigate({ to: '/quiz' });
      return;
    }
    setStepIndex((prev) => prev + 1);
  };

  const Step = STEPS[stepIndex] ?? OnboardingStep6;
  return <Step onContinue={goNext} />;
}
